// Browser polyfills
import '@babel/polyfill';
import 'regenerator-runtime/runtime';

import React, { useCallback, useState } from 'react';
// biw
import { Bridge } from '@nackle/bridge';
import { routeHistory, Route, Link } from '@nackle/biw-web-router';
import { Layout, Result } from 'antd';
import NackleMenu from '@nackle/menu';
import { setLanguageBundle } from '@nackle/intl-tools';
import {
    AppHeader,
    CMXService,
    AppError,
    ActivityIndicator,
    applyRequestMiddleware,
    authRequestMiddleware,
    authRouteMiddleware
} from '@nackle/studio-tools';
import UnsavedChangesModal from 'shared/UnsavedChangesModal';
import ErrorBoundary from './components/shared/error-boundry/error-boundry.jsx';
// api
import { apiGetSolutions } from './services/solution.js';
// auth
import { hasPermission } from './auth/permissions.js';
import auth from './auth/app-auth.js';
// routing
import { routeList, bundleRoutingMiddleware } from './app-routing.jsx';
// styles
// import 'antd/dist/antd.less';
import './styles/app.less';

const App = () => {
    
    const [ appLoaded, setAppLoaded ] = useState( false );
    const [ routeLoaded, setRouteLoaded ] = useState( false );
    const [ solution, setSolution ] = useState( {} );
    const [ noSolutionError, setNoSolutionError ] = useState( false );
    const [ user, setUser ] = useState( {} );

    const handleSolutions = async () => {
        const solutionsData = await apiGetSolutions();
        const solutions = solutionsData.solutions;
        if ( solutions.length === 0 ) {
            setNoSolutionError( true );
        } else {
            setSolution( solutions[ 0 ] );
        }
    };

    const handleSignIn = useCallback( async ( state ) => {
        setRouteLoaded( false );

        const match = window.location.pathname.match( /\b[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-4[a-fA-F0-9]{3}-[89abAB][a-fA-F0-9]{3}-[a-fA-F0-9]{12}\b/ );

        if ( match ) {
            await auth.delegate( match[ 0 ] );
        } else {
            routeHistory.push( '/' );
        }

        await routeHistory.refresh();

        await handleSolutions();

        const { locale } = state.claims;
        const cmxService = new CMXService( auth.state );
        const bundle = await cmxService.getBundle( [ 'navigation', 'studio-in-context-help' ], locale );

        await setLanguageBundle( locale, bundle );

        setUser( state );
        setAppLoaded( true );
        setRouteLoaded( true );
    }, [] );

    const handleCompanyChange = useCallback( ( route ) => async ( company ) => {
        setRouteLoaded( false );
        await auth.delegate( company.id );

        await handleSolutions();

        routeHistory.push( `/${ company.id }` );
        setRouteLoaded( true );
    }, [] );

    return (
        <AppError>
            <Route render={ ( route ) => {
                // set the title
                document.title = route && route.match ? route.match.title : 'Home';
                // get the companyId from the route
                const companyId = route.state?.params?.companyId;
                // permissions
                // const permissionToView = route.match && route.match.permission ? hasPermission( route.match.permission ) : true;

                return (
                    <Bridge
                        auth={ auth }
                        onSignIn={ handleSignIn }
                    >
                        {
                            appLoaded ? (
                                <Layout className="root-layout">
                                    <AppHeader
                                        companyId={ companyId }
                                        auth={ auth }
                                        onCompanyChange={ handleCompanyChange( route ) }
                                    />
                                    <Layout>
                                        <NackleMenu
                                            auth={ auth }
                                            cmxBundleLoaded={ appLoaded }
                                        />
                                        { routeLoaded ? (
                                            <Layout className="page-layout">
                                                <ErrorBoundary isUmbrella={ false } companyId={ companyId }>
                                                    <Layout.Content className="app-content">
                                                        { route.match  ?
                                                            React.cloneElement( route.component, {
                                                                key: companyId,
                                                                user,
                                                                auth,
                                                                companyId,
                                                                solutionId: solution.id,
                                                                hasPermission,
                                                                permissions: route.match.permissions
                                                            } )
                                                            // React.cloneElement( route.component, { companyId, user, accessToken } )
                                                            : route.match ? (
                                                                <Result
                                                                    status="403"
                                                                    title="Access Denied"
                                                                    subTitle="Sorry, you do not have permissions for the page you visited."
                                                                    extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
                                                                />
                                                            ) : noSolutionError ? (
                                                                <Result
                                                                    status="404"
                                                                    title="404"
                                                                    subTitle="Sorry, no solution found for this company."
                                                                    extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
                                                                />
                                                            ) : (
                                                                <Result
                                                                    status="404"
                                                                    title="404"
                                                                    subTitle="Sorry, the page you visited does not exist."
                                                                    extra={ <Link to="/" className="ant-btn ant-btn-primary">Back Home</Link> }
                                                                />
                                                        ) }
                                                    </Layout.Content>
                                                </ErrorBoundary>
                                            </Layout>    
                                        ) : ( 
                                            <ActivityIndicator />
                                        ) }
                                        
                                    </Layout>
                                </Layout>
                            ) : (
                                <ActivityIndicator />
                            )
                        }
                    </Bridge>
                );
            } } />
        </AppError>
    );
};

/*******************************************************************************
 *  2. Connect Routing
 */
routeHistory.applyRoutes( routeList );
routeHistory.applyMiddleware( [
    authRouteMiddleware( auth ),
    bundleRoutingMiddleware,
    UnsavedChangesModal.routerMiddleware,
] );

/*******************************************************************************
 *  3. Connect Auth to Fetch
 */
applyRequestMiddleware( authRequestMiddleware( auth ) );


/*******************************************************************************
 * 4. Export the app for rendering
 */

export default App;